import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock, Mail, Eye, EyeOff, Shield, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    senha: "",
    codigo_2fa: ""
  });

  // Verificar se já está logado
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      verificarToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verificarToken = async (token) => {
    try {
      const response = await axios.get(`${API}/api/auth/verificar?token=${token}`);
      if (response.data.valid) {
        navigate("/");
      }
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("usuario");
      localStorage.removeItem("empresa");
      localStorage.removeItem("permissoes");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.senha) {
      toast.error("Preencha email e senha");
      return;
    }

    if (requires2FA && !formData.codigo_2fa) {
      toast.error("Digite o código do Google Authenticator");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, formData);
      
      if (response.data.success) {
        // Salvar dados no localStorage
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("usuario", JSON.stringify(response.data.usuario));
        localStorage.setItem("empresa", JSON.stringify(response.data.empresa));
        localStorage.setItem("permissoes", JSON.stringify(response.data.permissoes));
        
        // Mostrar aviso se foi deslogado de outro dispositivo
        if (response.data.foi_deslogado_outro_dispositivo) {
          toast.warning("Você foi deslogado em outro dispositivo", {
            duration: 5000,
            icon: <AlertTriangle className="w-5 h-5 text-amber-500" />
          });
        } else {
          toast.success(`Bem-vindo, ${response.data.usuario.nome}!`);
        }
        
        // Redirecionar baseado no perfil do usuário
        const perfil = response.data.usuario?.perfil;
        const perfisFuncionarios = ["ATENDENTE", "CORRETOR", "TELEMARKETING", "GESTOR_ADMINISTRATIVO", "GESTOR_OPERACIONAL"];
        if (perfisFuncionarios.includes(perfil)) {
          navigate("/clientes");  // Funcionários e Corretores vão para Clientes
        } else {
          navigate("/");  // Admin e Master vão para Dashboard
        }
      } else if (response.data.message === "2FA_REQUIRED" || response.data.requires_2fa) {
        // Usuário precisa digitar código 2FA
        setRequires2FA(true);
        toast.info("Digite o código do Google Authenticator");
      } else {
        toast.error(response.data.message || "Erro ao fazer login");
      }
    } catch (error) {
      console.error("Erro no login:", error);
      toast.error(error.response?.data?.message || "Erro ao conectar ao servidor");
    } finally {
      setLoading(false);
    }
  };

  const handleCodigo2FAChange = (e) => {
    const valor = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData({ ...formData, codigo_2fa: valor });
  };

  const voltarParaLogin = () => {
    setRequires2FA(false);
    setFormData({ ...formData, codigo_2fa: "" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="flex items-center justify-center p-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl">
              <div className="text-center">
                <span className="text-white font-bold text-2xl tracking-wider">STILOCRED</span>
              </div>
            </div>
          </div>
          <p className="text-slate-400 mt-2">Sistema de CRM Gestão de Consignado</p>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl text-center">
              {requires2FA ? "Verificação de Segurança" : "Acesse sua conta"}
            </CardTitle>
            <CardDescription className="text-center">
              {requires2FA 
                ? "Digite o código do Google Authenticator" 
                : "Digite suas credenciais para entrar no sistema"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!requires2FA ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="senha">Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="senha"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        value={formData.senha}
                        onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                        data-testid="login-senha"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Campo 2FA */}
                  <div className="text-center space-y-4">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <Shield className="w-8 h-8 text-green-600" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="codigo_2fa">Código de verificação</Label>
                      <Input
                        id="codigo_2fa"
                        type="text"
                        placeholder="000000"
                        className="text-center text-2xl font-mono tracking-widest"
                        maxLength={6}
                        value={formData.codigo_2fa}
                        onChange={handleCodigo2FAChange}
                        autoFocus
                        data-testid="login-2fa-code"
                      />
                      <p className="text-xs text-gray-500">
                        Abra o Google Authenticator e digite o código de 6 dígitos
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={voltarParaLogin}
                  >
                    ← Voltar
                  </Button>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                disabled={loading || (requires2FA && formData.codigo_2fa.length !== 6)}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {requires2FA ? "Verificando..." : "Entrando..."}
                  </>
                ) : (
                  requires2FA ? "Verificar e Entrar" : "Entrar"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Problemas para acessar?</p>
              <p className="text-xs mt-1">Entre em contato com o administrador</p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          © 2025 StiloCred. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
